import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { useOutletContext, useSearchParams } from '@remix-run/react';
import { useTranslation } from 'react-i18next';

import LoginOrSubscribe from '~/components/CTAs/LoginOrSubscribe';
import PostPaddingContainer from '~/components/Layout/PostPaddingContainer';
import Text from '~/components/Text';

import { CommentSectionState } from '~/interfaces/comment';
import type { OutletContext } from '~/interfaces/outlet_context';
import type { Post } from '~/interfaces/post';
import type { Publication } from '~/interfaces/publication';
import type { Subscription } from '~/interfaces/subscriptions';

import usePaginatedComments from '~/hooks/useLoaders/usePaginatedComments';

import CommentAction from './CommentAction';
import CommentsFeed from './CommentFeed';
import CommentLoader from './CommentLoader';
import CommentOptionsOrderingDropdown from './CommentOrderingOptionsDropdown';
import MockCommentBox from './MockCommentBox';

interface Props {
  post: Post;
  subscription?: Subscription;
  publication: Publication;
}

const CommentsSection = ({ post, subscription, publication }: Props) => {
  const { t } = useTranslation();
  const { admin } = useOutletContext<OutletContext>();

  const [searchParams, setSearchParams] = useSearchParams();
  const showBackToPost = searchParams.get('comments') === 'true';

  // Fetch comments for the post
  // This does not run if comments are disabled on this post or on the publication level
  // This also runs on the client so it is not page blocking to render the post. Comments are loaded in the background
  const {
    initialFetched,
    comments,
    optimisticallyRenderNewComment,
    optimisticallyDeleteTopLevelComment,
    commentOrder,
    reorderComments,
    loadMore,
    isLoadingMore,
    remainingCount,
    hasNextPage,
  } = usePaginatedComments({
    postId: post.id,
  });

  const isLoggedInAsAdmin = Boolean(admin);
  const isPremiumCommentsOnly = post.comments_state === CommentSectionState.PREMIUM;
  const isLoggedIn = Boolean(subscription);
  const hasNoComments = !comments?.length;
  const isBannedFromPostComments = Boolean(subscription?.comment_post_bans?.includes(post.id));
  const hasActiveCommentBan = Boolean(subscription?.comment_publication_ban) || isBannedFromPostComments;
  const commentsLocked = post.comments_state === CommentSectionState.LOCKED || !isLoggedIn || hasActiveCommentBan;

  const renderCommentAction = (!commentsLocked && Boolean(subscription)) || isLoggedInAsAdmin;
  const renderMockCommentBox = !isLoggedIn && !isLoggedInAsAdmin;

  const postPadding = publication.web_template.post_padding;
  const initialCommentOrder = publication?.web_template?.comments_ordering;

  return (
    <div className="pb-8">
      <PostPaddingContainer classNames="mx-auto max-w-2xl" postPadding={postPadding}>
        {showBackToPost && (
          <button
            type="button"
            className="flex items-center pt-8 pb-2 text-sm text-wt-primary"
            onClick={() => setSearchParams({})}
          >
            <ChevronLeftIcon className="w-3 h-3 mr-1" />
            <span>{post.web_title}</span>
          </button>
        )}
        <div className="flex items-center justify-between gap-4 py-2">
          <Text as="h4" size="xl" theme="content" weight="bold" className="leading-none">
            {t('comments.replying.reply')}
          </Text>
          <CommentOptionsOrderingDropdown
            reorderComments={reorderComments}
            commentOrder={commentOrder || initialCommentOrder}
          />
        </div>
        {renderCommentAction && (
          <div className="pb-4">
            <CommentAction
              postId={post.id}
              rows={4}
              onCommentCreated={optimisticallyRenderNewComment}
              subscription={subscription}
              isPremiumCommentsOnly={isPremiumCommentsOnly}
            />
          </div>
        )}
        {renderMockCommentBox && (
          <div className="pb-4">
            <MockCommentBox publication={publication} />
            <LoginOrSubscribe />
          </div>
        )}
        {initialFetched && !hasNoComments && (
          <>
            <CommentsFeed
              initialCommentsFetched={initialFetched}
              comments={comments}
              postId={post.id}
              optimisticallyDeleteComment={optimisticallyDeleteTopLevelComment}
              subscription={subscription}
              commentsLocked={commentsLocked && !isLoggedInAsAdmin}
              isPremiumCommentsOnly={isPremiumCommentsOnly}
            />
            {initialFetched && hasNextPage && (
              <div className="flex justify-center py-8">
                <button type="button" className="text-xs text-wt-primary" onClick={loadMore}>
                  {isLoadingMore ? (
                    <span>{t('comments.loading.loading')}...</span>
                  ) : (
                    <span>
                      {t('comments.loading.load_more')} ({remainingCount})
                    </span>
                  )}
                </button>
              </div>
            )}
          </>
        )}
        {!initialFetched && <CommentLoader />}
      </PostPaddingContainer>
    </div>
  );
};

export default CommentsSection;
